import { h } from 'preact'
import { useState } from 'preact/hooks'
import { formatMarkdown, escapeHtml } from '../markdown'

const AVATAR = (
  <div class="w-7 h-7 rounded-full bg-gradient-to-br from-[#1e3a8a] to-[#c9a961] flex items-center justify-center flex-shrink-0 mt-0.5">
    <span class="text-white text-xs font-bold">K</span>
  </div>
)

export default function Message({ message, animate, onFeedback }) {
  if (message.type === 'error') {
    return <ErrorMessage content={message.content} />
  }

  if (message.type === 'context_note') {
    return <ContextNote content={message.content} pillLabel={message.metadata?.pill_label} />
  }

  if (message.type === 'system') {
    return <SystemMessage content={message.content} />
  }

  if (message.role === 'user') {
    return <UserMessage content={message.content} animate={animate} />
  }

  return (
    <AssistantMessage
      content={message.content}
      animate={animate}
      messageId={message.id}
      initialFeedback={message.metadata?.feedback}
      onFeedback={onFeedback}
    />
  )
}

function UserMessage({ content, animate }) {
  return (
    <div class={`flex justify-end mb-3 chat-message${animate ? ' animate-slide-up' : ''}`}>
      <div class="max-w-[80%] bg-[#1e3a8a] text-white rounded-2xl rounded-br-md px-4 py-2.5 shadow-sm">
        <p class="text-sm leading-relaxed m-0">{content}</p>
      </div>
    </div>
  )
}

function AssistantMessage({ content, animate, messageId, initialFeedback, onFeedback }) {
  const [feedback, setFeedback] = useState(initialFeedback || null)
  const canRate = !!(messageId && onFeedback)

  function rate(value) {
    if (feedback === value) return
    setFeedback(value)
    onFeedback(messageId, value)
  }

  return (
    <div class={`flex items-start gap-2.5 mb-3 chat-message${animate ? ' animate-slide-up' : ''}`}>
      {AVATAR}
      <div class="chat-bubble-assistant group max-w-[80%] bg-white rounded-2xl rounded-bl-md px-4 py-2.5 shadow-sm border border-gray-100">
        <div
          class="text-sm leading-relaxed text-gray-800"
          dangerouslySetInnerHTML={{ __html: formatMarkdown(content) }}
        />
        {canRate && (
          <div class={`flex gap-1 mt-1.5 justify-end transition-opacity ${feedback ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
            <button
              onClick={() => rate('up')}
              aria-label="Helpful"
              title="Helpful"
              class={`p-0.5 rounded text-xs ${feedback === 'up' ? 'text-green-600' : 'text-gray-300 hover:text-green-500'}`}
            >
              <svg class="w-3.5 h-3.5" fill={feedback === 'up' ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6.633 10.25c.806 0 1.533-.446 2.031-1.08a9.041 9.041 0 0 1 2.861-2.4c.723-.384 1.35-.956 1.653-1.715a4.498 4.498 0 0 0 .322-1.672V2.75a.75.75 0 0 1 .75-.75 2.25 2.25 0 0 1 2.25 2.25c0 1.152-.26 2.243-.723 3.218-.266.558.107 1.282.725 1.282m0 0h3.126c1.026 0 1.945.694 2.054 1.715.045.422.068.85.068 1.285a11.95 11.95 0 0 1-2.649 7.521c-.388.482-.987.729-1.605.729H13.48c-.483 0-.964-.078-1.423-.23l-3.114-1.04a4.501 4.501 0 0 0-1.423-.23H5.904m10.598-9.75H14.25M5.904 18.5c.083.205.173.405.27.602.197.4-.078.898-.523.898h-.908c-.889 0-1.713-.518-1.972-1.368a12 12 0 0 1-.521-3.507c0-1.553.295-3.036.831-4.398C3.387 9.953 4.167 9.5 5 9.5h1.053c.472 0 .745.556.5.96a8.958 8.958 0 0 0-1.302 4.665c0 1.194.232 2.333.654 3.375Z" />
              </svg>
            </button>
            <button
              onClick={() => rate('down')}
              aria-label="Not helpful"
              title="Not helpful"
              class={`p-0.5 rounded text-xs ${feedback === 'down' ? 'text-red-500' : 'text-gray-300 hover:text-red-400'}`}
            >
              <svg class="w-3.5 h-3.5" fill={feedback === 'down' ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" style="transform: rotate(180deg);">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6.633 10.25c.806 0 1.533-.446 2.031-1.08a9.041 9.041 0 0 1 2.861-2.4c.723-.384 1.35-.956 1.653-1.715a4.498 4.498 0 0 0 .322-1.672V2.75a.75.75 0 0 1 .75-.75 2.25 2.25 0 0 1 2.25 2.25c0 1.152-.26 2.243-.723 3.218-.266.558.107 1.282.725 1.282m0 0h3.126c1.026 0 1.945.694 2.054 1.715.045.422.068.85.068 1.285a11.95 11.95 0 0 1-2.649 7.521c-.388.482-.987.729-1.605.729H13.48c-.483 0-.964-.078-1.423-.23l-3.114-1.04a4.501 4.501 0 0 0-1.423-.23H5.904m10.598-9.75H14.25M5.904 18.5c.083.205.173.405.27.602.197.4-.078.898-.523.898h-.908c-.889 0-1.713-.518-1.972-1.368a12 12 0 0 1-.521-3.507c0-1.553.295-3.036.831-4.398C3.387 9.953 4.167 9.5 5 9.5h1.053c.472 0 .745.556.5.96a8.958 8.958 0 0 0-1.302 4.665c0 1.194.232 2.333.654 3.375Z" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

function ContextNote({ content, pillLabel }) {
  return (
    <div>
      <AssistantMessage content={content} animate={false} />
      <div class="flex justify-center mb-2 chat-message animate-slide-up">
        <div class="chat-context-note">
          <svg class="w-3 h-3 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
            <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
          </svg>
          {pillLabel || 'Browsing'}
        </div>
      </div>
    </div>
  )
}

function ErrorMessage({ content }) {
  return (
    <div class="flex items-start gap-2.5 mb-3 animate-slide-up chat-message">
      <div class="w-7 h-7 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
        <svg class="w-3.5 h-3.5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
        </svg>
      </div>
      <div class="max-w-[80%] bg-red-50 rounded-2xl rounded-bl-md px-4 py-2.5 border border-red-100">
        <p class="text-sm text-red-700 m-0">{content}</p>
      </div>
    </div>
  )
}

function SystemMessage({ content }) {
  return (
    <div class="flex justify-center mb-3 chat-message animate-slide-up">
      <div class="px-4 py-2 rounded-full border" style="background: linear-gradient(135deg, #eff6ff, #fef3c7); border-color: #bfdbfe;">
        <p class="text-xs text-gray-600 m-0 flex items-center gap-1.5">
          <svg class="w-3.5 h-3.5 text-[#c9a961]" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
          {content}
        </p>
      </div>
    </div>
  )
}
